import { config } from '@/app/config'
import { registerPushSubscription, removePushSubscription } from '@/data/notifications'
import { supabase } from '@/data/supabase'

/**
 * Notifications push sur cet appareil (Web Push, PWA).
 * - `unavailable` : non configuré (clé publique absente) ou service worker absent (développement) ;
 * - `unsupported` : navigateur sans push ;
 * - `install-first` : iPhone ou iPad, le push n'existe qu'une fois l'app ajoutée à l'écran
 *   d'accueil (iOS 16.4 ou plus) ;
 * - `denied` : refusé dans les réglages du navigateur ;
 * - `off` / `on` : prêt, désactivé ou activé sur cet appareil.
 */
export type PushState = 'unavailable' | 'unsupported' | 'install-first' | 'denied' | 'off' | 'on'

function isApple(): boolean {
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
}

function standalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean }
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(base64url.length / 4) * 4, '=')
  const raw = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index)
  return bytes
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  return (await navigator.serviceWorker.getRegistration()) ?? null
}

export async function pushState(): Promise<PushState> {
  if (!config.vapidPublicKey) return 'unavailable'
  if (!('PushManager' in window) || !('Notification' in window)) {
    return isApple() && !standalone() ? 'install-first' : 'unsupported'
  }
  if (Notification.permission === 'denied') return 'denied'
  const current = await registration()
  if (!current) return 'unavailable'
  return (await current.pushManager.getSubscription()) ? 'on' : 'off'
}

/** Demande l'autorisation, abonne l'appareil et l'enregistre pour le compte connecté. */
export async function enablePush(): Promise<PushState> {
  const key = config.vapidPublicKey
  const current = await registration()
  if (!key || !current) return 'unavailable'
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
  const subscription =
    (await current.pushManager.getSubscription()) ??
    (await current.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(key),
    }))
  await registerPushSubscription(supabase, subscription.toJSON())
  return 'on'
}

/** Désabonne cet appareil (préférence ou déconnexion) ; sans erreur si rien n'était actif. */
export async function disablePush(): Promise<void> {
  const current = await registration()
  const subscription = await current?.pushManager.getSubscription()
  if (!subscription) return
  try {
    await removePushSubscription(supabase, subscription.endpoint)
  } finally {
    await subscription.unsubscribe()
  }
}
